import { useEffect, useRef, useState } from 'react';

export default function HomeCTA() {
  const [visible, setVisible] = useState(false);
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('submitting');
    const form = e.currentTarget;
    const formData = new FormData(form);
    const params = new URLSearchParams();
    formData.forEach((value, key) => params.append(key, value.toString()));
    try {
      const response = await fetch('https://readdy.ai/api/form/d7o4trokbgouofj9opm0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });
      if (response.ok) { setFormStatus('success'); form.reset(); }
      else setFormStatus('error');
    } catch { setFormStatus('error'); }
    setTimeout(() => setFormStatus('idle'), 3000);
  };

  return (
    <section ref={ref} className="py-20 md:py-28 bg-white">
      <div className="w-full px-4 md:px-8 lg:px-16">
        <div className="max-w-5xl mx-auto">
          {/* Top label */}
          <div className="text-center mb-12">
            <h2
              className={`text-2xl md:text-4xl font-bold text-[#1E5FAD] mb-4 transition-all duration-600 delay-100 ${
                visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
              }`}
            >
              您的营销费用，现在还是"黑盒"吗？
            </h2>
            <p
              className={`text-sm md:text-base text-[#666666] max-w-2xl mx-auto transition-all duration-600 delay-200 ${
                visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
              }`}
            >
              快消行业普遍存在费用不透明、终端数据缺失、活动执行低效三大顽疾。融慧数科提供
              <strong className="text-[#1E5FAD]">营销数字化专属客服</strong>，帮您找到最关键的改善切入点。
            </p>
          </div>

          {/* Card */}
          <div
            className={`bg-[#F5F7FA] rounded-2xl overflow-hidden border border-gray-100 transition-all duration-600 delay-300 ${
              visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
            style={{ boxShadow: '0 2px 24px rgba(30,95,173,0.06)' }}
          >
            <div className="flex flex-col lg:flex-row">
              {/* Left: Benefits */}
              <div
                className="lg:w-5/12 p-8 md:p-10 text-white relative overflow-hidden"
                style={{ background: 'linear-gradient(135deg, #1E5FAD 0%, #154A8A 100%)' }}
              >
                {/* BG decoration */}
                <div className="absolute top-[-40px] right-[-40px] w-48 h-48 rounded-full opacity-10"
                  style={{ background: 'radial-gradient(circle, #00AEEF, transparent)' }} />
                <div className="absolute bottom-[-20px] left-[-20px] w-32 h-32 rounded-full opacity-8"
                  style={{ background: 'radial-gradient(circle, #00AEEF, transparent)' }} />

                <h3 className="text-xl md:text-2xl font-bold text-white mb-6 relative z-10">
                  预约后您将获得
                </h3>
                <div className="space-y-4 relative z-10">
                  {[
                    { icon: 'ri-search-eye-line', title: '营销费用透明化诊断', desc: '全面梳理现有费用管控漏洞' },
                    { icon: 'ri-bar-chart-2-line', title: '终端数据化评估报告', desc: '量化终端执行效率与改善空间' },
                    { icon: 'ri-road-map-line', title: '数字化转型路线图', desc: '定制化的落地实施建议' },
                    { icon: 'ri-team-line', title: '专属顾问1对1服务', desc: '行业专家深度沟通与答疑' },
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-white/10 shrink-0 mt-0.5">
                        <i className={`${item.icon} text-[#00AEEF] text-base`} />
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-white">{item.title}</p>
                        <p className="text-xs text-white/60 mt-0.5">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Right: Form */}
              <div className="lg:w-7/12 p-8 md:p-10 bg-white">
                <h3 className="text-lg md:text-xl font-bold text-[#1E5FAD] mb-6">
                  立即预约专属客服
                </h3>

                <form data-readdy-form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-[#333333] mb-1.5">姓名 <span className="text-red-400">*</span></label>
                      <input
                        type="text"
                        name="name"
                        placeholder="请输入您的姓名"
                        required
                        className="w-full h-10 px-3.5 bg-[#F5F7FA] border border-gray-200 rounded-lg text-sm text-[#333333] placeholder:text-[#999] focus:outline-none focus:border-[#00AEEF] focus:bg-white transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-[#333333] mb-1.5">公司名称 <span className="text-red-400">*</span></label>
                      <input
                        type="text"
                        name="company"
                        placeholder="请输入公司名称"
                        required
                        className="w-full h-10 px-3.5 bg-[#F5F7FA] border border-gray-200 rounded-lg text-sm text-[#333333] placeholder:text-[#999] focus:outline-none focus:border-[#00AEEF] focus:bg-white transition-colors"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-[#333333] mb-1.5">手机号 <span className="text-red-400">*</span></label>
                    <input
                      type="tel"
                      name="phone"
                      placeholder="请输入手机号"
                      required
                      className="w-full h-10 px-3.5 bg-[#F5F7FA] border border-gray-200 rounded-lg text-sm text-[#333333] placeholder:text-[#999] focus:outline-none focus:border-[#00AEEF] focus:bg-white transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-[#333333] mb-1.5">当前最大痛点</label>
                    <select
                      name="pain_point"
                      className="w-full h-10 px-3.5 bg-[#F5F7FA] border border-gray-200 rounded-lg text-sm text-[#333333] focus:outline-none focus:border-[#00AEEF] focus:bg-white transition-colors cursor-pointer"
                    >
                      <option value="">请选择（可选）</option>
                      <option value="费用不透明">营销费用不透明</option>
                      <option value="终端数据缺失">终端数据缺失</option>
                      <option value="活动执行低效">活动执行低效</option>
                      <option value="渠道管控难">渠道管控难</option>
                      <option value="消费者洞察不足">消费者洞察不足</option>
                    </select>
                  </div>

                  <button
                    type="submit"
                    disabled={formStatus === 'submitting'}
                    className="w-full h-11 bg-[#00AEEF] text-white font-semibold rounded-lg hover:bg-[#0099d4] transition-colors disabled:opacity-70 whitespace-nowrap text-sm mt-2"
                  >
                    {formStatus === 'submitting' ? '提交中...' : formStatus === 'success' ? '预约成功！顾问将尽快联系您' : formStatus === 'error' ? '提交失败，请重试' : '立即预约专属客服'}
                  </button>
                </form>

                <p className="text-xs text-[#999] text-center mt-3">
                  提交后顾问将在24小时内与您联系 · 完全免费，无任何隐性费用
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
